'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { Typography } from 'lighthouse';

import AttributeFiltersView from '@/widgets/AttributeFilters/AttributeFiltersView';
import CatalogProductCard from '@/widgets/CatalogProductCard';
import { CatalogProductCardLoading } from '@/widgets/CatalogProductCard/CatalogProductCardLoading';
import { catalogItemFromOffer, catalogItemFromProduct, catalogSpecsFromGuarantee } from '@/widgets/CatalogProductCard/mappers';
import SectionHeading from '@/widgets/ServiceLanding/SectionHeading';

import { useCategoryAttributeFilters } from '@/hooks/filters/useCategoryAttributeFilters';

import { ATTRIBUTE_FILTER_COPY, ATTRIBUTE_FILTER_QUERY_KEY, BRAND_FILTER_QUERY_KEY } from '@/utilities/attributeFilters/constants';
import { buildServiceCategoryHref } from '@/utilities/buildServiceCategoryHref';

import { useCategoryStore } from '@/stores/Category/useCategoryStore';
import { useCityStore } from '@/stores/city/useCityStore';

import { batteryBadges, isSuggestedBattery } from '@/constants/batteryProduct';
import { PRODUCTS_SORT_AVAILABLE_DESC_AND_SELLING_PRICE_ASC_ID } from '@/constants/common';
import { DEFAULT_PRODUCT_OFFER_SORT_BY } from '@/constants/productSort';

import { PageableProducts } from '../PageableProducts';
import SwiperProducts from '../SwiperProducts';
import { ProductsProps } from './models';

import { getProductsBatteryByVehicleFiltersService } from '@/client/services/core/productBattery/methods';
import { getFilteredProductsService } from '@/client/services/vendor/methods';
import { ProductType } from '@/server/services/core/product/models';
import { ProductBatteryType } from '@/server/services/core/productBattery/models';
import { OfferContent, ProductOfferSortBy } from '@/server/services/vendor/models';

export const ProductsHydrated = ({
    contentData: { listStyle, pageSize, title, filterUuid, sourceType },
    filterOptions,
    isOnlyOnePage = false,
    productType = 'BATTERY',
    isDark = false,
    initialCityId,
    initialProducts,
    isLast,
    totalElements,
    strapiFilters,
    categoryNavItems,
    categoryNavBreadcrumb,
    mobileCarousel
}: ProductsProps) => {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const cityId = useCityStore((state) => state.cityId);
    const setCategoryName = useCategoryStore((state) => state.setCategoryName);
    const setTotalElements = useCategoryStore((state) => state.setTotalElements);
    const categoryId = filterOptions?.categoryIds?.[0] ?? strapiFilters.categoryIds?.[0];
    const displayTitle = title;
    const isCarousel = listStyle === 'scroll';
    const hasCategoryNav = Boolean(categoryNavItems?.length || categoryNavBreadcrumb?.length);
    const hasPresetBrands = Boolean((strapiFilters.brandIds ?? filterOptions?.brandIds)?.length);
    const withBrands = !isCarousel && sourceType !== 'battery' && !hasPresetBrands;
    const hasUrlFilters =
        !isCarousel &&
        sourceType !== 'battery' &&
        (searchParams.getAll(ATTRIBUTE_FILTER_QUERY_KEY).length > 0 || (withBrands && searchParams.getAll(BRAND_FILTER_QUERY_KEY).length > 0));
    const attributeFilters = useCategoryAttributeFilters({
        categoryId: isCarousel || sourceType === 'battery' ? null : categoryId,
        withBrands,
        syncUrl: !isCarousel
    });
    const attributeFilterKey = [attributeFilters.attributeFilterParams.join('|'), attributeFilters.brandIds.join(',')].join('#');
    const hasSidebarFilters = attributeFilters.isLoading || attributeFilters.hasFilters;
    const productsGridClassName = hasSidebarFilters
        ? 'grid w-full grid-cols-1 gap-4 pb-2 sm:grid-cols-2 lg:grid-cols-3'
        : 'grid w-full grid-cols-1 gap-4 pb-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
    const [sortBy, setSortBy] = useState<ProductOfferSortBy>(DEFAULT_PRODUCT_OFFER_SORT_BY);

    const [products, setProducts] = useState<ProductBatteryType[] | ProductType[] | OfferContent[]>(() => (hasUrlFilters ? [] : initialProducts));
    const [isLastPage, setIsLastPage] = useState(isLast);
    const [offerTotalElements, setOfferTotalElements] = useState(totalElements ?? null);
    const [isProductsLoading, setIsProductsLoading] = useState(hasUrlFilters);
    const [fetchedFingerprint, setFetchedFingerprint] = useState<string | null>(null);
    const productsRequestIdRef = useRef(0);

    const preferredBrandIds = strapiFilters.brandIds ?? filterOptions?.brandIds;
    const requestBrandIds = attributeFilters.brandIds.length ? attributeFilters.brandIds : preferredBrandIds;
    const preferredCategoryIds = strapiFilters.categoryIds ?? filterOptions?.categoryIds;
    const productListRequestFingerprint = JSON.stringify({
        cityId,
        preferredBrandIds,
        preferredCategoryIds,
        sourceType,
        filterUuid,
        pageSize,
        productType,
        strapiFilters,
        filterOptions,
        attributeFilterKey,
        sortBy
    });

    const rootCategorySlug = pathname.split('/').filter(Boolean)[1];
    const navigateToCategory = (slug: string) => {
        const params = new URLSearchParams(searchParams.toString());
        params.delete('subCategorySlug');
        params.delete('categoryId');
        const query = params.toString();
        const categoryPath = buildServiceCategoryHref(rootCategorySlug, slug);
        router.push(query ? `${categoryPath}?${query}` : categoryPath, { scroll: false });
    };

    const categoryNav = hasCategoryNav
        ? {
              items: categoryNavItems ?? [],
              activeId: categoryNavBreadcrumb?.at(-1)?.id ?? null,
              breadcrumb: categoryNavBreadcrumb,
              onSelect: navigateToCategory,
              onBreadcrumbSelect: navigateToCategory
          }
        : undefined;

    const handleGetProducts = async (nextCityId: number, requestId: number, fingerprint: string) => {
        setIsProductsLoading(true);
        try {
            const response = await getProductsBatteryByVehicleFiltersService({
                ...strapiFilters,
                ...filterOptions,
                cityId: nextCityId,
                page: 0,
                size: pageSize,
                sortOption: PRODUCTS_SORT_AVAILABLE_DESC_AND_SELLING_PRICE_ASC_ID
            });

            if (requestId !== productsRequestIdRef.current) {
                return;
            }

            setIsLastPage(response.data.last);
            setProducts(filterUuid ? response.data.content.filter((product) => product.uuid !== filterUuid) : response.data.content);
            setFetchedFingerprint(fingerprint);
        } catch {
            if (requestId === productsRequestIdRef.current) {
                setProducts([]);
                setFetchedFingerprint(fingerprint);
            }
        } finally {
            if (requestId === productsRequestIdRef.current) {
                setIsProductsLoading(false);
            }
        }
    };

    const handleGetFilteredProducts = async (nextCityId: number, requestId: number, fingerprint: string) => {
        setIsProductsLoading(true);
        try {
            const response = await getFilteredProductsService({
                ...(requestBrandIds?.length && { brandIds: requestBrandIds }),
                ...(preferredCategoryIds?.length && { categoryIds: preferredCategoryIds }),
                cityId: nextCityId,
                page: 0,
                size: pageSize,
                sortBy,
                ...(attributeFilters.attributeFilterParams.length ? { attributeFilter: attributeFilters.attributeFilterParams } : {})
            });

            if (requestId !== productsRequestIdRef.current) {
                return;
            }

            setIsLastPage(response.data.last);
            setProducts(filterUuid ? response.data.content.filter((product) => product.productUuid !== filterUuid) : response.data.content);
            setOfferTotalElements(response.data.totalElements ?? null);
            setFetchedFingerprint(fingerprint);
        } catch {
            if (requestId === productsRequestIdRef.current) {
                setProducts([]);
                setOfferTotalElements(null);
                setFetchedFingerprint(fingerprint);
            }
        } finally {
            if (requestId === productsRequestIdRef.current) {
                setIsProductsLoading(false);
            }
        }
    };

    const isServerSnapshotValid =
        sourceType === 'battery' &&
        !hasUrlFilters &&
        attributeFilterKey === '#' &&
        sortBy === DEFAULT_PRODUCT_OFFER_SORT_BY &&
        cityId === initialCityId;

    useEffect(() => {
        const requestId = ++productsRequestIdRef.current;

        if (attributeFilters.isLoading && sourceType !== 'battery') {
            return;
        }

        if (fetchedFingerprint === productListRequestFingerprint) {
            return;
        }

        if (isServerSnapshotValid) {
            setProducts(initialProducts);
            setIsLastPage(isLast);
            setFetchedFingerprint(productListRequestFingerprint);
            setIsProductsLoading(false);
            return;
        }

        const fingerprint = productListRequestFingerprint;

        if (sourceType === 'battery') {
            void handleGetProducts(cityId, requestId, fingerprint);
        } else {
            void handleGetFilteredProducts(cityId, requestId, fingerprint);
        }
    }, [productListRequestFingerprint, attributeFilters.isLoading, fetchedFingerprint, isServerSnapshotValid, initialProducts, isLast]);

    useEffect(() => {
        // router.replace on filter change makes the server component re-render with new (unfiltered) initialProducts.
        // While client-side filters/sort/city are active, those must not overwrite the client-fetched list.
        if (!isServerSnapshotValid) {
            return;
        }

        setProducts(initialProducts);
        setIsLastPage(isLast);
    }, [initialProducts, isLast, isServerSnapshotValid]);

    useEffect(() => {
        setCategoryName(title || '');
        setTotalElements(sourceType === 'battery' ? (totalElements ?? null) : offerTotalElements);
    }, [setCategoryName, setTotalElements, title, totalElements, sourceType, offerTotalElements]);

    const isListLoading =
        isProductsLoading ||
        (sourceType !== 'battery' && attributeFilters.isLoading && hasUrlFilters) ||
        (fetchedFingerprint !== productListRequestFingerprint && !(isServerSnapshotValid && fetchedFingerprint === null));
    const emptyMessage = attributeFilters.activeCount ? ATTRIBUTE_FILTER_COPY.emptyResults : 'محصولی یافت نشد!';

    const productListContent =
        listStyle === 'scroll' ? (
            isListLoading ? (
                <div className={`${productsGridClassName} ${mobileCarousel ? 'max-sm:[&>div:nth-child(n+2)]:hidden' : ''}`}>
                    <CatalogProductCardLoading />
                </div>
            ) : !!products.length ? (
                <SwiperProducts products={products} sourceType={sourceType} mobileCarousel={mobileCarousel} />
            ) : (
                <div className='flex justify-center'>
                    <Typography variant='caption' className='w-full py-8 text-center text-gray-400' align='center'>
                        {emptyMessage}
                    </Typography>
                </div>
            )
        ) : (
            <>
                <div className={productsGridClassName}>
                    {isListLoading ? (
                        <CatalogProductCardLoading />
                    ) : products.length ? (
                        products.map((product) => {
                            if (product.discountPercentage !== 99) {
                                if (sourceType === 'battery') {
                                    const batteryProduct = product as ProductBatteryType;
                                    return (
                                        <CatalogProductCard
                                            key={batteryProduct.uuid}
                                            item={catalogItemFromProduct(batteryProduct, { categoryId })}
                                            badge={isSuggestedBattery(batteryProduct) ? 'پیشنهاد ازکی‌سرویس' : undefined}
                                            specs={[
                                                ...batteryBadges(batteryProduct).map((badge) => badge.title),
                                                ...(catalogSpecsFromGuarantee(batteryProduct.guarantee) ?? [])
                                            ]}
                                        />
                                    );
                                }

                                const offerProduct = product as OfferContent;
                                return (
                                    <CatalogProductCard
                                        key={offerProduct.productUuid}
                                        item={catalogItemFromOffer(offerProduct)}
                                        showSelectButton={false}
                                    />
                                );
                            }
                        })
                    ) : (
                        <Typography
                            variant='caption'
                            className='col-span-full w-full rounded-lg border border-dashed border-N200 py-8 text-blackLowEmphasis'
                            align='center'>
                            {emptyMessage}
                        </Typography>
                    )}
                </div>
                <div>
                    {listStyle === 'pagination' && !isOnlyOnePage && !isListLoading && (
                        <PageableProducts
                            key={productListRequestFingerprint}
                            cityId={cityId}
                            lastPage={isLastPage}
                            pageSize={pageSize || 10}
                            sourceType={sourceType}
                            filterOptions={{ ...strapiFilters, ...filterOptions }}
                            attributeFilter={attributeFilters.attributeFilterParams}
                            brandIds={attributeFilters.brandIds}
                            sortBy={sortBy}
                            wideGrid={!hasSidebarFilters}
                        />
                    )}
                </div>
            </>
        );

    return (
        <section className={displayTitle ? 'py-8 md:py-12' : ''}>
            {!!displayTitle && <SectionHeading title={displayTitle} dark={isDark} />}

            {!isCarousel && (sourceType !== 'battery' || hasCategoryNav) ? (
                <AttributeFiltersView
                    filters={attributeFilters}
                    className='px-2'
                    categoryNav={categoryNav}
                    sortBy={sourceType !== 'battery' ? sortBy : undefined}
                    onSortChange={sourceType !== 'battery' ? setSortBy : undefined}>
                    {productListContent}
                </AttributeFiltersView>
            ) : (
                productListContent
            )}
        </section>
    );
};
